import React, { useEffect, useState } from "react";
import {
  LuMessageCircle,
  LuInbox,
  LuUserX,
  LuTriangleAlert,
  LuTimer,
  LuCircleCheck,
  LuBot,
  LuMessagesSquare,
  LuChevronDown,
  LuChevronUp,
  LuFileSpreadsheet,
  LuX,
} from "react-icons/lu";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import KpiTileGrid from "../../components/Dashboard/KpiTileGrid";
import { statusMeta } from "../Leads/leadMeta";

// The support manager's view of the WhatsApp inbox (GET vendorWhatsappChatKpis): what needs attention right
// now, and how fast the team replies over a period. Shown to owners and admins only (the API refuses others).

const ICON = { fontSize: "20px" };
const REFRESH_MS = 60 * 1000;
const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const rangeFor = (days) => {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  return { from: ymd(from), to: ymd(to) };
};

// 45s, 5m 12s, 1h 04m
const duration = (sec) => {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${pad(sec % 60)}s`;
  return `${Math.floor(m / 60)}h ${pad(m % 60)}m`;
};

const CUSTOMER_COLOR = "#C9194A";
const TEAM_COLOR = "#94A3B8";
const BAR_AREA = 64;

const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: "2-digit", month: "short" });
const hourRange = (h) => `${pad(h)}:00 - ${pad((h + 1) % 24)}:00`;

// A column chart with a number above each bar (when there are few enough to read), labels under some of them,
// and the series stacked. items: [{ ...fields }]; series: [{ key, color, name }].
const compact = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, "")}k` : String(n));

const ColumnChart = ({ items, series, labelOf, tickEvery, ariaLabel, alwaysShowValues = false }) => {
  const totalOf = (it) => series.reduce((sum, sr) => sum + (it[sr.key] || 0), 0);
  const max = Math.max(1, ...items.map(totalOf));
  const showValues = alwaysShowValues || items.length <= 14;
  // Many narrow bars (e.g. 24 hours): smaller, abbreviated numbers so neighbours do not collide.
  const dense = items.length > 14;
  return (
    <div role="img" aria-label={ariaLabel}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: BAR_AREA + 16, borderBottom: "1px solid #E5E7EB" }}>
        {items.map((it, i) => {
          const total = totalOf(it);
          const tip = `${labelOf(it)}: ${series.map((sr) => `${it[sr.key] || 0} ${sr.name}`).join(", ")}`;
          return (
            <div key={i} title={tip} style={{ flex: 1, minWidth: 6, maxWidth: 36, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "stretch", height: "100%" }}>
              {showValues && total > 0 && (
                <div style={{ fontSize: dense ? 9 : 10, color: "#6B7280", textAlign: "center", lineHeight: "14px", whiteSpace: "nowrap", overflow: "visible" }}>{dense ? compact(total) : total}</div>
              )}
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: total ? Math.max(3, (total / max) * BAR_AREA) : 0 }}>
                {series.map((sr) => {
                  const v = it[sr.key] || 0;
                  return v ? <div key={sr.key} style={{ height: `${(v / total) * 100}%`, background: sr.color, minHeight: 1 }} /> : null;
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 3, marginTop: 3 }}>
        {items.map((it, i) => (
          <div key={i} style={{ flex: 1, minWidth: 6, maxWidth: 36, fontSize: 10, color: "#6B7280", textAlign: "center", whiteSpace: "nowrap", overflow: "visible" }}>
            {i % tickEvery === 0 ? labelOf(it, true) : ""}
          </div>
        ))}
      </div>
    </div>
  );
};

const Legend = ({ entries }) => (
  <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#6B7280", marginBottom: 4 }}>
    {entries.map((e) => (
      <span key={e.name} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: e.color }} />
        {e.name}
      </span>
    ))}
  </div>
);

const ChartCard = ({ title, takeaway, children }) => (
  <div style={{ flex: "1 1 320px", minWidth: 0 }}>
    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#374151" }}>{title}</div>
    <div style={{ fontSize: 11.5, color: "#6B7280", margin: "2px 0 8px" }}>{takeaway}</div>
    {children}
  </div>
);

const NoData = () => (
  <div style={{ border: "1px dashed #E5E7EB", borderRadius: 8, padding: "18px 12px", fontSize: 12, color: "#9CA3AF", textAlign: "center" }}>
    No messages in this period
  </div>
);

// Two small charts under the tiles: when customers write (to decide who is on shift) and how busy each day was.
export const VolumeCharts = ({ data, days }) => {
  const byHour = data.by_hour || [];
  const byDay = data.by_day || [];
  const hourTotal = byHour.reduce((sum, h) => sum + h.inbound, 0);
  const peak = byHour.reduce((best, h) => (h.inbound > best.inbound ? h : best), { hour: 0, inbound: 0 });
  const busiestDay = byDay.reduce((best, d) => (d.inbound + d.outbound > best.total ? { date: d.date, total: d.inbound + d.outbound } : best), { date: null, total: 0 });
  const dayTotal = byDay.reduce((sum, d) => sum + d.inbound + d.outbound, 0);

  return (
    <div style={{ display: "flex", gap: 28, flexWrap: "wrap", margin: "-2px 0 12px", paddingTop: 4, borderTop: "1px solid #F3F4F6" }}>
      <ChartCard
        title="When customers write"
        takeaway={
          hourTotal
            ? `Busiest hour: ${hourRange(peak.hour)} with ${peak.inbound} customer message${peak.inbound === 1 ? "" : "s"} (${Math.round((peak.inbound / hourTotal) * 100)}% of ${hourTotal}). Hour of day, last ${days} days.`
            : `Customer messages by hour of day, last ${days} days.`
        }
      >
        {hourTotal ? (
          <ColumnChart
            items={byHour}
            series={[{ key: "inbound", color: CUSTOMER_COLOR, name: "customer messages" }]}
            labelOf={(h, short) => (short ? pad(h.hour) : hourRange(h.hour))}
            tickEvery={3}
            alwaysShowValues
            ariaLabel="Customer messages by hour of day"
          />
        ) : (
          <NoData />
        )}
      </ChartCard>

      <ChartCard
        title="Messages per day"
        takeaway={
          dayTotal
            ? `${dayTotal} messages over ${byDay.length} day${byDay.length === 1 ? "" : "s"} with activity. Busiest day: ${dayLabel(busiestDay.date)} (${busiestDay.total}).`
            : `Customer and team messages per day, last ${days} days.`
        }
      >
        {dayTotal ? (
          <>
            <Legend
              entries={[
                { name: "Customer", color: CUSTOMER_COLOR },
                { name: "Team / AI", color: TEAM_COLOR },
              ]}
            />
            <ColumnChart
              items={byDay}
              series={[
                { key: "inbound", color: CUSTOMER_COLOR, name: "from customers" },
                { key: "outbound", color: TEAM_COLOR, name: "from team / AI" },
              ]}
              labelOf={(d) => dayLabel(d.date)}
              tickEvery={Math.max(1, Math.ceil(byDay.length / 7))}
              ariaLabel="Messages per day"
            />
          </>
        ) : (
          <NoData />
        )}
      </ChartCard>
    </div>
  );
};

const ChatKpiStrip = ({ isMobile, refreshKey, onOpenUnread }) => {
  // Chatting is the point of this page, so the stats start collapsed on every visit; expand them when needed.
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState(() => rangeFor(30).from);
  const [exportTo, setExportTo] = useState(() => rangeFor(1).to);
  const [exporting, setExporting] = useState(false);

  // The export for the chosen dates, built from the APIs the page already uses:
  //  - chats started in the dates, by status; those without a lead are "inquiries" (the Inquiry / Leads tab rule)
  //  - leads created from Channels in the dates, by status, and the inquiry -> lead conversion
  //  - first-reply speed of the team and when customers write (from the chat figures)
  // Open chats come whole; Closed / Resolved ones come from the Closed list (the API caps that at 200).
  const exportFunnel = async () => {
    if (!exportFrom || !exportTo || exportFrom > exportTo) {
      toast.error("Pick a valid start and end date");
      return;
    }
    setExporting(true);
    try {
      const [kpi, leadsRes, allLeadsRes, openRes, closedRes, teamRes] = await Promise.all([
        apicall.get(`vendorWhatsappChatKpis?from=${exportFrom}&to=${exportTo}`),
        apicall.get(`leads?source_type=Channels&startDate=${exportFrom}&endDate=${exportTo}`),
        apicall.get("leads?source_type=Channels"),
        apicall.get("vendorWhatsappChats"),
        apicall.get(`vendorWhatsappChats?closed=1&limit=200&from=${exportFrom}`),
        apicall.get("getVendorTeamUsers").catch(() => null),
      ]);
      if (!kpi?.success) throw new Error("kpi");
      const list = (r) => (Array.isArray(r?.data) ? r.data : []);
      const key = (p) => String(p || "").replace(/\D/g, "").slice(-9);
      // An agent is shown by first name. Some accounts have the text "undefined" saved as a name part: skip it.
      const firstName = (v) =>
        String(v || "").split(/\s+/).find((w) => w && !["undefined", "null"].includes(w.toLowerCase())) || "";
      const pct =(a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
      const localDay = (v) => {
        const d = v ? new Date(v) : null;
        return d && !isNaN(d) ? ymd(d) : "";
      };

      // Leads in the dates: one per customer, carrying the status of their newest lead.
      const byCustomer = new Map();
      list(leadsRes).forEach((l) => {
        const k = key(l.phone) || `lead-${l.id}`;
        const cur = byCustomer.get(k);
        if (!cur || (l.created_at && (!cur.createdRaw || l.created_at > cur.createdRaw))) {
          byCustomer.set(k, { name: l.customer_name, phone: l.phone, createdRaw: l.created_at, status: statusMeta(l.status).label });
        }
      });
      const leads = [...byCustomer.values()];

      // Chats started in the dates; a chat whose customer has a Channels lead is a lead chat, otherwise an inquiry.
      const leadKeys = new Set(list(allLeadsRes).map((l) => key(l.phone)).filter(Boolean));
      const seen = new Set();
      const allChats = [...list(openRes), ...list(closedRes)].filter((c) => (seen.has(c.messages_link_id) ? false : seen.add(c.messages_link_id)));
      const chats = allChats
        .map((c) => ({
          name: c.customer_name || "",
          phone: c.customer_phone || "",
          started: localDay(c.time),
          status: c.chat_status || "Open",
          isLead: leadKeys.has(key(c.customer_phone)),
          assignee: firstName(c.employee_name),
        }))
        .filter((c) => c.started && c.started >= exportFrom && c.started <= exportTo);
      const inquiries = chats.filter((c) => !c.isLead);
      const leadChats = chats.filter((c) => c.isLead);
      const isClosed = (c) => c.status === "Closed" || c.status === "Resolved";

      const STATUS_ORDER = ["Open", "Pending", "Follow up", "Booked", "Resolved", "Closed"];


      const p = kpi.data?.period || {};
      const sla = kpi.data?.sla_minutes ?? 15;
      const answered = (p.human_first_response_count ?? 0) + (p.ai_first_response_count ?? 0);
      const withinSla = p.within_sla_pct == null ? null : Math.round((p.within_sla_pct / 100) * answered);
      const followUps = chats.filter((c) => c.status === "Follow up" || c.status === "Pending").length;
      const closedInquiries = inquiries.filter(isClosed).length;
      const closedLeadChats = leadChats.filter(isClosed).length;

      // ---- agent wise: first-reply speed from the chat figures, overdue chats from the open chats, leads from the leads
      const agents = new Map(); // id -> { name, answered, avgSec, within, overdue, created, booked }
      // Names of the team (id -> name), used whenever a row only carries an id.
      const teamNames = new Map();
      (Array.isArray(teamRes?.data) ? teamRes.data : []).forEach((u) => {
        const first = firstName(u.first_name) || firstName(u.name) || firstName(u.full_name);
        if (u.id && first) teamNames.set(String(u.id), first);
      });
      // Only a real team member with a name gets a row; a chat with nobody assigned has no agent.
      const agentOf = (id, name) => {
        if (!id) return null;
        const k = String(id);
        const known = firstName(name) || teamNames.get(k) || "";
        if (!known) return agents.get(k) || null;
        if (!agents.has(k)) agents.set(k, { name: known, answered: 0, avgSec: null, within: 0, overdue: 0, created: 0, booked: 0 });
        return agents.get(k);
      };
      (kpi.data?.by_agent || []).forEach((r) => {
        const row = agentOf(r.agent_id, r.agent_name);
        if (!row) return;
        row.answered = r.answered;
        row.avgSec = r.avg_first_reply_sec;
        row.within = r.within_sla;
      });
      allChats.forEach((c) => c.employee_id && agentOf(c.employee_id, String(c.employee_name || "").trim()));
      list(allLeadsRes).forEach((l) => l.assigned_agent_id && agentOf(l.assigned_agent_id, String(l.assigned_agent_name || "").trim()));
      // Overdue: open chats whose last message is the customer's and has waited longer than the reply target.
      const now = Date.now();
      list(openRes).forEach((c) => {
        const customerSpokeLast = [0, 3, 5].includes(Number(c.direction));
        const at = c.created_at ? new Date(c.created_at).getTime() : 0;
        if (customerSpokeLast && at && now - at > sla * 60 * 1000) { const row = agentOf(c.employee_id, String(c.employee_name || "").trim()); if (row) row.overdue++; }
      });
      // Leads created in the dates, by whoever created them.
      list(leadsRes).forEach((l) => {
        const row = agentOf(l.created_by);
        if (!row) return;
        row.created++;
        if (statusMeta(l.status).label === "Booked") row.booked++;
      });
      const agentHeader = ["Agent name", "Avg. First Reply", `Replied within ${sla} min`, "Overdue chats", "Created leads", "Booking conversion", "Chats answered", "Leads booked"];
      const agentRows = [...agents.values()]
        .sort((x, y) => y.answered - x.answered || y.created - x.created || y.overdue - x.overdue)
        .map((g) => [
          g.name,
          g.avgSec == null ? "—" : duration(g.avgSec),
          g.answered ? `${g.within} of ${g.answered} (${pct(g.within, g.answered)})` : "—",
          g.overdue,
          g.created,
          g.created ? `${pct(g.booked, g.created)} (${g.booked} of ${g.created})` : "\u2014",
          g.answered,
          g.booked,
        ]);

      const count = (rows, field, value) => rows.filter((r) => r[field] === value).length;
      const chatStatuses = [...new Set([...STATUS_ORDER.filter((n) => chats.some((c) => c.status === n)), ...chats.map((c) => c.status)])];
      const leadStatuses = [...new Set(leads.map((l) => l.status))];
      const metricNames = [
        "Total chats received", "Created leads", "Avg reply time", "Unassigned chats", "Total leads",
        "Total Follow-up (pending)", "Total inquiries closed", `Replied within ${sla} min`, "AI handled chats",
        "Chats closed (total)", "Chats closed - inquiry", "Chats closed - leads", "Inquiry to leads conversion",
      ];
      const metricValues = [
        chats.length, leads.length, duration(p.human_first_response_avg_sec), chats.filter((c) => !c.assignee).length, leadChats.length,
        followUps, closedInquiries,
        withinSla == null ? "\u2014" : `${withinSla} of ${answered} (${p.within_sla_pct}%)`,
        p.ai_first_response_count ?? 0,
        closedInquiries + closedLeadChats, closedInquiries, closedLeadChats,
        chats.length ? `${pct(leads.length, chats.length)} (${leads.length} of ${chats.length})` : "\u2014",
      ];
      const summary = [
        ["Channels report", `${exportFrom} to ${exportTo}`],
        [],
        metricNames,
        metricValues,
        [],
        [],
        ["Agent wise report"],
        agentHeader,
        ...(agentRows.length ? agentRows : [["No agent activity in these dates"]]),
        [],
        [],
        ["Status wise: chats and inquiries"],
        ["Status", "Chats received", "Inquiries", "% of chats"],
        ...chatStatuses.map((n) => [n, count(chats, "status", n), count(inquiries, "status", n), pct(count(chats, "status", n), chats.length)]),
        ["Total", chats.length, inquiries.length, chats.length ? "100%" : "\u2014"],
        [],
        ["Status wise: leads"],
        ["Status", "Leads", "% of leads"],
        ...leadStatuses.map((n) => [n, count(leads, "status", n), pct(count(leads, "status", n), leads.length)]),
        ["Total", leads.length, leads.length ? "100%" : "\u2014"],
      ];
      const agentSheet = [
        ["Agent wise report", exportFrom + " to " + exportTo],
        [],
        agentHeader,
        ...(agentRows.length ? agentRows : [["No agent activity in these dates"]]),
      ];
      const when = [
        ["Customer messages by hour of day"],
        ["Hour", "Customer messages"],
        ...(kpi.data?.by_hour || []).map((h) => [hourRange(h.hour), h.inbound]),
        [],
        ["Messages per day"],
        ["Date", "From customers", "From team / AI"],
        ...(kpi.data?.by_day || []).map((d) => [d.date, d.inbound, d.outbound]),
      ];
      const chatRows = [
        ["Customer", "Phone", "Chat started", "Chat status", "Type", "Assigned to"],
        ...chats.map((c) => [c.name, c.phone, c.started, c.status, c.isLead ? "Lead" : "Inquiry", c.assignee]),
      ];
      const leadRows = [
        ["Customer", "Phone", "Lead created", "Status", "Booked"],
        ...leads.map((l) => [l.name || "", l.phone || "", l.createdRaw ? String(l.createdRaw).slice(0, 10) : "", l.status, l.status === "Booked" ? "Yes" : "No"]),
      ];

      const sheet = (rows, cols) => {
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws["!cols"] = cols.map((wch) => ({ wch }));
        return ws;
      };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet(summary, [26, 22, 22, 22, 22, 22, 22, 24, 22, 20, 22, 22, 28]), "Summary");
      XLSX.utils.book_append_sheet(wb, sheet(agentSheet, [26, 18, 26, 16, 16, 26, 16, 14]), "Agent wise report");
      XLSX.utils.book_append_sheet(wb, sheet(when, [28, 18, 16]), "When customers message");
      XLSX.utils.book_append_sheet(wb, sheet(chatRows, [28, 18, 14, 14, 10, 24]), "Chats");
      XLSX.utils.book_append_sheet(wb, sheet(leadRows, [28, 18, 14, 14, 10]), "Leads");
      XLSX.writeFile(wb, `channels_${exportFrom}_to_${exportTo}.xlsx`);
      setExportOpen(false);
    } catch (e) {
      toast.error("Could not export right now");
    } finally {
      setExporting(false);
    }
  };

  const toggleOpen = () => setOpen((v) => !v);

  // A slow refresh while it is on screen: the numbers move as the team works.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), REFRESH_MS);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const { from, to } = rangeFor(days);
    apicall
      .get(`vendorWhatsappChatKpis?from=${from}&to=${to}`)
      .then((res) => {
        if (cancelled) return;
        setData(res?.success ? res.data : null);
        setFailed(!res?.success);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, days, tick, refreshKey]);

  const now = data?.now;
  const period = data?.period;
  const sla = data?.sla_minutes ?? 15;

  const dash = (v) => (data ? v : null);
  const tiles = [
    {
      key: "unread",
      label: "Unread chats",
      icon: <LuMessageCircle style={{ ...ICON, color: "#037FE8" }} />,
      color: "#037FE8",
      value: dash(now?.unread_chats),
      weekKey: "unread",
      onClick: onOpenUnread,
    },
    { key: "awaiting", label: "Awaiting reply", icon: <LuInbox style={{ ...ICON, color: "#92007E" }} />, color: "#92007E", value: dash(now?.awaiting_reply), weekKey: null },
    { key: "unassigned", label: "Unassigned chats", icon: <LuUserX style={{ ...ICON, color: "#6C757D" }} />, color: "#6C757D", value: dash(now?.unassigned), weekKey: null },
    {
      key: "breach",
      label: `Waiting over ${sla} min`,
      icon: <LuTriangleAlert style={{ ...ICON, color: "#C9194A" }} />,
      color: "#C9194A",
      value: dash(now?.sla_breaches),
      weekKey: null,
    },
    {
      key: "first",
      label: "Avg first reply (team)",
      icon: <LuTimer style={{ ...ICON, color: "#FF8C00" }} />,
      color: "#FF8C00",
      value: dash(duration(period?.human_first_response_avg_sec)),
      weekKey: null,
    },
    {
      key: "within",
      label: `Replied in ${sla} min`,
      icon: <LuCircleCheck style={{ ...ICON, color: "#28A745" }} />,
      color: "#28A745",
      value: dash(period?.within_sla_pct == null ? "—" : `${period.within_sla_pct}%`),
      weekKey: null,
    },
    // Only where the AI Assistant is installed (the API sends null otherwise).
    ...(now && now.with_ai != null
      ? [
          {
            key: "ai",
            label: "With AI / handed to team",
            icon: <LuBot style={{ ...ICON, color: "#4F46E5" }} />,
            color: "#4F46E5",
            value: `${now.with_ai} / ${now.handed_to_team}`,
            weekKey: null,
          },
        ]
      : []),
    {
      key: "volume",
      label: "Messages in / out",
      icon: <LuMessagesSquare style={{ ...ICON, color: "#037FE8" }} />,
      color: "#037FE8",
      value: dash(`${period?.messages_in ?? 0} / ${period?.messages_out ?? 0}`),
      weekKey: null,
    },
  ];

  return (
    <div style={{ background: "#fff", borderBottom: "1px solid #e9ecef", padding: open ? "6px 16px 0" : "0 16px", flexShrink: 0, position: "relative", zIndex: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 32 }}>
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open}
          style={{ border: "none", background: "none", padding: 0, fontSize: 12.5, fontWeight: 600, color: "#374151", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          {open ? <LuChevronUp /> : <LuChevronDown />}
          Inbox performance
        </button>
        {open && (
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Period for reply times and volume"
            style={{ fontSize: 12, border: "1px solid #E5E7EB", borderRadius: 6, padding: "2px 6px", color: "#374151", background: "#fff" }}
          >
            {RANGES.map((r) => (
              <option key={r.days} value={r.days}>
                Last {r.label}
              </option>
            ))}
          </select>
        )}
        {open && failed && !data && <span style={{ fontSize: 12, color: "#9CA3AF" }}>Figures are not available yet.</span>}
        <div style={{ marginLeft: "auto", position: "relative" }}>
          <button
            type="button"
            onClick={() => setExportOpen((v) => !v)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#1a3a5f", background: "#fff", border: "1px solid #D1D5DB", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}
          >
            <LuFileSpreadsheet size={15} color="#1D6F42" /> Export
          </button>
          {exportOpen && (
            <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 300, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: 14, width: 250 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#374151" }}>Channels export</span>
                <button
                  type="button"
                  aria-label="Close"
                  title="Close"
                  onClick={() => !exporting && setExportOpen(false)}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, border: "none", background: "transparent", color: "#6B7280", borderRadius: 4, cursor: exporting ? "default" : "pointer", padding: 0 }}
                >
                  <LuX size={15} />
                </button>
              </div>
              <label style={{ display: "block", fontSize: 11.5, color: "#6B7280", marginBottom: 8 }}>
                Start date
                <input type="date" value={exportFrom} max={exportTo || undefined} onChange={(e) => setExportFrom(e.target.value)} style={{ display: "block", width: "100%", marginTop: 2, fontSize: 12.5, padding: "4px 6px", border: "1px solid #E5E7EB", borderRadius: 6 }} />
              </label>
              <label style={{ display: "block", fontSize: 11.5, color: "#6B7280", marginBottom: 10 }}>
                End date
                <input type="date" value={exportTo} min={exportFrom || undefined} onChange={(e) => setExportTo(e.target.value)} style={{ display: "block", width: "100%", marginTop: 2, fontSize: 12.5, padding: "4px 6px", border: "1px solid #E5E7EB", borderRadius: 6 }} />
              </label>
              <button
                type="button"
                onClick={exportFunnel}
                disabled={exporting}
                style={{ width: "100%", fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#d01c4c", border: "none", borderRadius: 6, padding: "7px 0", cursor: exporting ? "default" : "pointer", opacity: exporting ? 0.7 : 1 }}
              >
                {exporting ? "Preparing…" : "Download Excel"}
              </button>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div style={{ maxHeight: "40vh", overflowY: "auto" }}>
          <KpiTileGrid tiles={tiles} />
          {data && <VolumeCharts data={data} days={days} />}
        </div>
      )}
    </div>
  );
};

export default ChatKpiStrip;
